// Pure recurrence-rule expansion. No browser globals so it is unit-testable.
// A recurrence rule describes a repeating event; expansion produces concrete
// single-day event drafts. Large repeating sets (e.g. "work every weekday for a
// year") are described as ONE rule instead of hundreds of event objects — this
// is what keeps the AI proposal payload small and prevents JSON truncation.

import { CATEGORY_MAP } from "./categories";

export const VALID_FREQUENCIES = ["daily", "weekly"];

export function parseISODate(dateISO) {
  if (typeof dateISO !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(dateISO)) return null;
  const d = new Date(`${dateISO}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function weekdayOf(dateISO) {
  const d = parseISODate(dateISO);
  return d ? d.getDay() : null; // 0=Sun .. 6=Sat
}

export function addDays(dateISO, n) {
  const d = parseISODate(dateISO);
  if (!d) return null;
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

// --- Deterministic weekday guard (Package E) --------------------------------
// The application supplies calendar truth: if the user named specific weekday(s)
// but the AI enumerated dates that are uniformly off (the observed off-by-one),
// repair them deterministically. Fully self-contained; reuses weekdayOf/addDays.

// weekday index is getDay(): 0=Sun .. 6=Sat
const WEEKDAY_TOKENS = {
  0: ["sunday", "sunnuntai", "söndag", "sondag"],
  1: ["monday", "maanantai", "måndag", "mandag"],
  2: ["tuesday", "tiistai", "tisdag"],
  3: ["wednesday", "keskiviikko", "onsdag"],
  4: ["thursday", "torstai", "torsdag"],
  5: ["friday", "perjantai", "fredag"],
  6: ["saturday", "lauantai", "lördag", "lordag"],
};

// Set of weekday indices (0..6) explicitly named in a free-text message.
export function weekdayIndicesInText(text) {
  const s = String(text || "").toLowerCase();
  const found = new Set();
  for (const [idx, tokens] of Object.entries(WEEKDAY_TOKENS)) {
    if (tokens.some((tok) => s.includes(tok))) found.add(Number(idx));
  }
  return found;
}

// If the user named weekday(s) and dated create proposals disagree, repair them
// deterministically (the application supplies calendar truth). Two safe stages:
//  (1) uniform off-by-one shift — when the WHOLE batch is wrong and a single shift
//      d in {+1,-1,+2,-2} lands them all on a named weekday (preserves count/times);
//  (2) re-derive — for a repeating group (>=2 plain creates sharing title+time)
//      whose dates don't all match, rebuild the group's dates as EVERY named-weekday
//      date within the group's own [min,max] span (fixes messy partial enumerations
//      like MWF returned off-by-one). Fail-safe: never touches correct batches, and
//      only rebuilds when it can produce at least one valid date.
export function datesInRangeForWeekdays(startISO, endISO, weekdaySet) {
  const out = [];
  let cur = startISO;
  const end = endISO;
  let guard = 0;
  while (cur && cur <= end && guard < 4000) {
    const wd = weekdayOf(cur);
    if (wd != null && weekdaySet.has(wd)) out.push(cur);
    cur = addDays(cur, 1);
    guard += 1;
  }
  return out;
}

export function repairProposalWeekdays(proposals, userMessage) {
  if (!Array.isArray(proposals) || !proposals.length) return proposals;
  const named = weekdayIndicesInText(userMessage);
  if (named.size === 0) return proposals;

  const isDatedCreate = (p) => p && p.kind === "create" && p.new && parseISODate(p.new.date) != null;
  const dated = proposals.filter(isDatedCreate);
  if (dated.length === 0) return proposals;

  const inSet = (dateISO, shift) => {
    const wd = weekdayOf(addDays(dateISO, shift));
    return wd != null && named.has(wd);
  };

  const sameMonth = (aISO, bISO) => String(aISO).slice(0, 7) === String(bISO).slice(0, 7);

  // Stage 1: uniform shift when the entire batch is wrong (kept within the SAME
  // month so we never turn e.g. "first Monday of next month" into the prior month).
  if (dated.every((p) => !inSet(p.new.date, 0))) {
    for (const d of [1, -1, 2, -2]) {
      if (dated.every((p) => inSet(p.new.date, d) && sameMonth(p.new.date, addDays(p.new.date, d)))) {
        return proposals.map((p) =>
          isDatedCreate(p) ? { ...p, new: { ...p.new, date: addDays(p.new.date, d) } } : p
        );
      }
    }
  }

  // Stage 2: re-derive repeating groups (plain creates only — never parent/child links).
  const keyOf = (p) =>
    [String(p.new.title || "").trim().toLowerCase(), p.new.startHour, p.new.startMinute,
     p.new.endHour, p.new.endMinute, p.new.category].join("|");
  const groups = new Map();
  for (const p of dated) {
    if (p.new.parentEventId || p.new.tempId) continue; // don't disturb linked structures
    const k = keyOf(p);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(p);
  }

  const replacements = new Map(); // first-proposal-ref -> array of rebuilt proposals
  const drop = new Set();
  for (const items of groups.values()) {
    if (items.length < 2) continue;
    if (items.every((p) => inSet(p.new.date, 0))) continue; // already all correct
    const isoDates = items.map((p) => p.new.date).sort();
    const valid = datesInRangeForWeekdays(isoDates[0], isoDates[isoDates.length - 1], named);
    if (!valid.length) continue;
    const template = items[0];
    const rebuilt = valid.map((date) => ({ ...template, new: { ...template.new, date } }));
    replacements.set(template, rebuilt);
    for (const p of items.slice(1)) drop.add(p);
  }
  if (replacements.size === 0) return proposals;

  const out = [];
  for (const p of proposals) {
    if (drop.has(p)) continue;
    if (replacements.has(p)) out.push(...replacements.get(p));
    else out.push(p);
  }
  return out;
}


export function daysBetween(aISO, bISO) {
  const a = parseISODate(aISO);
  const b = parseISODate(bISO);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

function timesOk(rule) {
  const s = rule.startHour * 60 + (rule.startMinute || 0);
  const e = rule.endHour * 60 + (rule.endMinute || 0);
  return e > s;
}

// Validate a recurrence rule. Returns { ok, error }.
export function validateRule(rule, maxDateRangeDays) {
  if (!rule || typeof rule !== "object") return { ok: false, error: "missing rule" };
  if (!rule.title || !String(rule.title).trim()) return { ok: false, error: "missing title" };
  if (!VALID_FREQUENCIES.includes(rule.frequency)) return { ok: false, error: "invalid frequency" };
  if (!CATEGORY_MAP[rule.category]) return { ok: false, error: "invalid category" };
  if (parseISODate(rule.startDate) == null) return { ok: false, error: "invalid startDate" };
  if (parseISODate(rule.endDate) == null) return { ok: false, error: "invalid endDate" };
  const span = daysBetween(rule.startDate, rule.endDate);
  if (span == null || span < 0) return { ok: false, error: "endDate before startDate" };
  if (span > maxDateRangeDays) return { ok: false, error: "date range too large" };
  if (
    !Number.isInteger(rule.startHour) || !Number.isInteger(rule.endHour) ||
    rule.startHour < 0 || rule.startHour > 23 || rule.endHour < 0 || rule.endHour > 24
  ) {
    return { ok: false, error: "invalid time" };
  }
  if (!timesOk(rule)) return { ok: false, error: "end must be after start" };
  if (rule.frequency === "weekly") {
    const days = Array.isArray(rule.daysOfWeek) ? rule.daysOfWeek : [];
    if (!days.length || days.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) {
      return { ok: false, error: "invalid daysOfWeek" };
    }
  }
  return { ok: true, error: null };
}

function draftFrom(rule, dateISO) {
  return {
    title: String(rule.title).trim(),
    date: dateISO,
    startHour: rule.startHour,
    startMinute: rule.startMinute || 0,
    endHour: rule.endHour,
    endMinute: rule.endMinute || 0,
    category: rule.category,
    priority: rule.priority ?? 3,
    description: rule.description || "",
    status: "planned",
  };
}

// Expand one rule into concrete drafts, stopping at `cap`.
// Returns { events, error, truncated }.
export function expandRecurrence(rule, cap, maxDateRangeDays) {
  const v = validateRule(rule, maxDateRangeDays);
  if (!v.ok) return { events: [], error: v.error, truncated: false };

  const events = [];
  const interval = Number.isInteger(rule.interval) && rule.interval > 0 ? rule.interval : 1;
  const span = daysBetween(rule.startDate, rule.endDate);
  const daySet = rule.frequency === "weekly" ? new Set(rule.daysOfWeek) : null;
  let truncated = false;

  for (let i = 0; i <= span; i += 1) {
    if (events.length >= cap) { truncated = true; break; }
    const dateISO = addDays(rule.startDate, i);
    if (rule.frequency === "daily") {
      if (interval > 1 && i % interval !== 0) continue;
      events.push(draftFrom(rule, dateISO));
    } else {
      // weekly
      const wd = weekdayOf(dateISO);
      if (!daySet.has(wd)) continue;
      if (interval > 1) {
        const weekIdx = Math.floor(i / 7);
        if (weekIdx % interval !== 0) continue;
      }
      events.push(draftFrom(rule, dateISO));
    }
  }
  return { events, error: null, truncated };
}
